import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSimilarReaders } from '@/hooks/useSimilarReaders';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { CoverImage } from './CoverImage';
import { StarRating } from './StarRating';

export function SimilarReadersFeed() {
  const { data: similarReaders, isLoading, refetch } = useSimilarReaders();
  const { toast } = useToast();
  const [followingStates, setFollowingStates] = useState<Record<string, boolean>>({});

  const handleFollow = async (userId: string, currentlyFollowing: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (currentlyFollowing) {
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', userId);
      } else {
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: userId,
            status: 'accepted'
          });
      }

      setFollowingStates(prev => ({ ...prev, [userId]: !currentlyFollowing }));
      refetch();

      toast({
        title: currentlyFollowing ? 'Unfollowed' : 'Following',
        description: currentlyFollowing 
          ? 'You are no longer following this user'
          : 'You are now following this reader',
      });
    } catch (error) {
      console.error('Follow error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update follow status',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!similarReaders || similarReaders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="mb-4 rounded-full bg-muted p-6">
          <BookOpen className="h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No matches yet</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          Rate more books to help us find readers with similar taste. The more you rate, the better your matches!
        </p>
        <Button asChild>
          <Link to="/profile">Rate Your Books</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {similarReaders.map((reader) => {
        const isFollowing = followingStates[reader.id] ?? reader.isFollowing;
        const latestReview = reader.recentReviews?.[0];

        return (
          <div key={reader.id} className="space-y-3">
            {/* User Header */}
            <div className="flex items-start justify-between gap-3">
              <Link 
                to={`/profile/${reader.username}`}
                className="flex items-center gap-3 flex-1 group"
              >
                <Avatar className="h-12 w-12 border-2 border-primary/20">
                  <AvatarImage src={reader.image_thumb || reader.image} alt={reader.name} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground">
                    {reader.name?.[0] || reader.username?.[0] || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {reader.name || `@${reader.username}`}
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      {reader.similarity_score}% match
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {reader.match_reason}
                  </p>
                </div>
              </Link>
              <Button
                variant={isFollowing ? "outline" : "default"}
                size="sm"
                onClick={() => handleFollow(reader.id, isFollowing)}
                className="shrink-0"
              >
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            </div>

            {/* Latest Review */}
            {latestReview && (
              <Link
                to={`/book/${latestReview.book_id}`}
                className="flex gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
              >
                <div className="shrink-0">
                  <CoverImage
                    bookId={latestReview.book_id}
                    title={latestReview.books.title}
                    author={latestReview.books.authors?.[0]}
                    coverUrl={latestReview.books.cover_url}
                    coverCacheUrl={latestReview.books.cover_cache_url}
                    size="xs"
                    className="w-12"
                  />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-start gap-2">
                    <BookOpen className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                        {latestReview.books.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {latestReview.books.authors?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StarRating 
                      value={latestReview.rating_star} 
                      size="sm" 
                      readonly 
                      showLabel={false}
                    />
                    <span className="text-xs text-muted-foreground">
                      {new Date(latestReview.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {latestReview.review_text && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {latestReview.review_text}
                    </p>
                  )}
                </div>
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}
